import { getAuthToken } from "bitcoin-auth";
import type { BapMasterBackup } from "bitcoin-backup";
import { isLegacyBackup, isType42Backup } from "bitcoin-backup";
import { BAP } from "bsv-bap";
import { syncBackupToStorage, updateBapIdsInBackup } from "./bap/backup";
import {
	enableNewWalletCloudBackup,
	markCloudBackupStale,
	markNewWalletCloudBackupPending,
} from "./bap/cloud-refresh";
import { getBAPInstance, syncIdentitiesFromChain } from "./bap/core";
import {
	type IdentityBackup,
	isSeedIdentityBackup,
	validateIdentityBackup,
} from "./bap/identity-backup";
import {
	readInitialDisplayName as readInitialDisplayNameFromStorage,
	readInitialProfileData,
	saveInitialProfileDraft,
} from "./bap/identity-draft";
import { readReissuedBackupOffer } from "./bap/reissued-backup";
import type { SeedBackup } from "./bap/seed-contract";
import { SeedProfileAdapter } from "./bap/seed-profile-adapter";
import {
	extractIdentityFromBackup,
	extractWifFromBackup,
	getAccountPubkey,
	initializeBAPFromBackup,
} from "./bap/utils";
import { signOutBetterAuthSession } from "./better-auth-session";
import { logger } from "./logger";
import {
	LEGACY_OAUTH_OWNER_RECOVERY_PATH,
	type LegacyOAuthOwnerRecoveryResponse,
	serializeLegacyOAuthOwnerRecoveryBody,
} from "./oauth/legacy-owner-recovery";
import {
	SIGMA_SIGNIN_REQUEST_PATH,
	SIGMA_SIGNUP_REQUEST_PATH,
	serializeSigmaSigninBody,
} from "./sigma-signin-contract";
import { clearSensitiveSessionStorage } from "./storage/sensitive-storage";
import { STORAGE_KEYS } from "./storage-keys";

export type SyncProgressCallback = (
	step: string,
	status: "in_progress" | "completed" | "error",
	error?: string
) => void;

/**
 * The display name captured by the signup form (INITIAL_PROFILE_DATA), or
 * null when none was captured (imports/restores).
 */
function readInitialDisplayName(): string | null {
	return readInitialDisplayNameFromStorage();
}

/**
 * Get identity name from onboarding form data stored in sessionStorage
 * Falls back to "My Identity" if not found
 */
function getInitialIdentityName(): string {
	const initialData = readInitialProfileData();
	return initialData?.alternateName || "My Identity";
}

/**
 * Create an auth token from a member backup
 * Member backups are the signing unit - they contain the WIF and BAP ID already
 * @param backup - Member backup (NOT master backup)
 * @param requestPath - The API endpoint path
 * @param body - Optional request body
 */
export function createAuthTokenFromBackup(
	backup: { wif: string; id: string },
	requestPath: string,
	body?: string
): string {
	const config: {
		privateKeyWif: string;
		requestPath: string;
		body?: string;
	} = {
		privateKeyWif: backup.wif,
		requestPath,
	};

	// Only add body if it's provided
	if (body) {
		config.body = body;
	}

	return getAuthToken(config);
}

interface SyncResult {
	success: boolean;
	count: number;
	error?: string;
	identities?: Array<{ bapId: string; counter: number; name?: string }>;
}

interface RegisterBapIdentityParams {
	bapId: string;
	name: string;
	counter: number;
	isPrimary: boolean;
	accountPubkey?: string;
}

/**
 * Extract account pubkey from a BAP identity
 */
function extractAccountPubkey(
	bap: InstanceType<typeof BAP>,
	bapId: string
): string | undefined {
	try {
		const member = bap.getId(bapId);
		if (member) {
			return getAccountPubkey(member);
		}
	} catch {
		logger.warn("Failed to extract account public key");
	}
	return undefined;
}

/**
 * Register a BAP identity with the server
 */
export async function registerBapIdentity(
	params: RegisterBapIdentityParams
): Promise<boolean> {
	try {
		const response = await fetch("/api/user/bap-ids", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(params),
		});
		if (!response.ok) {
			logger.error("BAP identity registration request failed");
			throw new Error("Failed to register Bitcoin identity");
		}

		return true;
	} catch (error) {
		logger.error("BAP identity registration failed");
		throw error;
	}
}

/**
 * Save initial profile data from onboarding flow.
 *
 * saveInitialProfileDraft reports failure instead of throwing and keeps the
 * captured data for a retry: a sync that runs before the session exists gets
 * a 401, and the typed name must survive for the post-sign-in registration
 * (OPL-4238).
 */
async function saveInitialProfileData(bapId: string): Promise<void> {
	const saved = await saveInitialProfileDraft(bapId);
	if (saved) {
		logger.info("Saved initial profile data for new wallet");
		return;
	}
	logger.warn(
		{ bapId },
		"Failed to save initial profile data; keeping it for retry"
	);
}

/**
 * Sync BAP IDs for a NEW Type42 wallet (no existing identities)
 */
async function syncNewType42Wallet(
	backup: BapMasterBackup & { rootPk: string },
	onProgress?: SyncProgressCallback
): Promise<SyncResult> {
	onProgress?.("Creating first identity", "in_progress");

	const bap = new BAP({ rootPk: backup.rootPk });
	const newIdentity = bap.newId();
	const bapId = newIdentity.bapId;
	const updatedIds = bap.exportIds();

	await syncBackupToStorage({ ...backup, ids: updatedIds });

	logger.info("Created first Bitcoin identity");
	onProgress?.("Created first identity", "completed");

	const accountPubkey = extractAccountPubkey(bap, bapId);

	onProgress?.("Registering identity", "in_progress");
	const identityName = getInitialIdentityName();
	// Persist the draft first: the server's registration resolves the stored
	// draft into the profile row (and the user's display name), so saving it
	// afterwards left a placeholder behind (OPL-4238).
	await saveInitialProfileData(bapId);
	await registerBapIdentity({
		bapId,
		name: identityName,
		counter: 0,
		isPrimary: true,
		accountPubkey,
	});

	onProgress?.("Registered identity successfully", "completed");
	return {
		success: true,
		count: 1,
		identities: [{ bapId, counter: 0, name: identityName }],
	};
}

/**
 * Sync BAP IDs for an existing Type42 wallet with multiple identities
 */
async function syncExistingType42Wallet(
	backup: BapMasterBackup & { rootPk: string; ids: string },
	onProgress?: SyncProgressCallback
): Promise<SyncResult> {
	const bap = new BAP({ rootPk: backup.rootPk });
	bap.importIds(backup.ids);
	const allIds = bap.listIds();
	logger.info(`Type 42 backup with ${allIds.length} existing identities`);
	onProgress?.(`Found ${allIds.length} identities in backup`, "completed");

	onProgress?.("Registering identities", "in_progress");
	let registered = 0;
	const registeredIdentities: Array<{
		bapId: string;
		counter: number;
		name?: string;
	}> = [];

	// A fresh signup arrives here with exactly the identity created before
	// sign-in; the display name the user typed must win over "Identity 1",
	// and the draft has to exist before registration resolves it (OPL-4238).
	const initialDisplayName = readInitialDisplayName();
	if (allIds.length > 0 && initialDisplayName) {
		await saveInitialProfileData(allIds[0]);
	}

	for (let i = 0; i < allIds.length; i++) {
		const currentBapId = allIds[i];
		const accountPubkey = extractAccountPubkey(bap, currentBapId);
		const idName =
			i === 0 && initialDisplayName ? initialDisplayName : `Identity ${i + 1}`;

		try {
			await registerBapIdentity({
				bapId: currentBapId,
				name: idName,
				counter: i,
				isPrimary: i === 0,
				accountPubkey,
			});
			registered++;
			registeredIdentities.push({
				bapId: currentBapId,
				counter: i,
				name: idName,
			});
			logger.info("Registered Type 42 Bitcoin identity");
		} catch {
			logger.warn("Failed to register Type 42 Bitcoin identity");
		}
	}

	logger.info(`Registered ${registered}/${allIds.length} Type 42 BAP IDs`);
	onProgress?.(`Registered ${registered} identities successfully`, "completed");
	return {
		success: registered > 0,
		count: registered,
		error:
			registered === 0 && allIds.length > 0
				? "Failed to register any Type 42 BAP IDs"
				: undefined,
		identities: registeredIdentities,
	};
}

/**
 * Sync BAP IDs from a legacy (xprv) backup
 */
async function syncLegacyBackupIds(
	backup: BapMasterBackup,
	onProgress?: SyncProgressCallback
): Promise<SyncResult> {
	onProgress?.("Discovering identities from backup", "in_progress");

	const bap = initializeBAPFromBackup(backup);
	if (!bap) {
		onProgress?.(
			"Failed to initialize BAP from backup",
			"error",
			"Failed to initialize BAP from backup"
		);
		return {
			success: false,
			count: 0,
			error: "Failed to initialize BAP from backup",
		};
	}

	const allIds = bap.listIds();
	logger.info(`Discovered ${allIds.length} BAP IDs from backup`);
	onProgress?.(`Found ${allIds.length} identities in backup`, "completed");

	onProgress?.("Registering identities", "in_progress");
	let registered = 0;
	const registeredIdentities: Array<{
		bapId: string;
		counter: number;
		name?: string;
	}> = [];

	for (let i = 0; i < allIds.length; i++) {
		const bapId = allIds[i];
		const member = bap.getId(bapId);
		onProgress?.(
			`Registering identity ${i + 1} of ${allIds.length}`,
			"in_progress"
		);

		let accountPubkey: string | undefined;
		try {
			if (member) {
				accountPubkey = getAccountPubkey(member);
			}
		} catch {
			logger.warn("Failed to extract account public key");
		}

		const profileName = `Identity ${i + 1}`;

		try {
			await registerBapIdentity({
				bapId,
				name: profileName,
				counter: i,
				isPrimary: i === 0,
				accountPubkey,
			});
			registered++;
			registeredIdentities.push({
				bapId,
				counter: i,
				name: profileName,
			});
			logger.info("Registered Bitcoin identity");
		} catch {
			logger.warn("Failed to register Bitcoin identity");
		}
	}

	logger.info(`Registered ${registered}/${allIds.length} BAP IDs`);
	onProgress?.(`Registered ${registered} identities successfully`, "completed");
	return {
		success: registered > 0,
		count: registered,
		error:
			registered === 0 && allIds.length > 0
				? "Failed to register any legacy BAP IDs"
				: undefined,
		identities: registeredIdentities,
	};
}

/** Explicit enrollment/registration uses exact persisted seed indices, never list positions. */
async function syncSeedInventory(
	backup: SeedBackup,
	onProgress?: SyncProgressCallback
): Promise<SyncResult> {
	const adapter = new SeedProfileAdapter(backup);
	const identities: Array<{ bapId: string; counter: number; name: string }> =
		[];
	for (const profile of backup.profiles) {
		const identity = adapter.getId(profile.bapId);
		if (!identity) {
			throw new Error("Invalid seed profile inventory");
		}
		const name =
			typeof profile.metadata?.name === "string"
				? profile.metadata.name
				: `Identity ${profile.index + 1}`;
		await registerBapIdentity({
			bapId: profile.bapId,
			counter: profile.index,
			name,
			isPrimary: profile === backup.profiles[0],
			accountPubkey: getAccountPubkey(identity),
		});
		identities.push({ bapId: profile.bapId, counter: profile.index, name });
	}
	onProgress?.(`Registered ${identities.length} seed profiles`, "completed");
	return { success: true, count: identities.length, identities };
}

/**
 * Discover all BAP IDs from backup and register them in database
 * This ensures all identities are available for switching/display
 *
 * @param backup - The BAP master backup to sync from
 * @param onProgress - Optional callback to report progress
 */
export async function syncBapIdsFromBackup(
	backup: IdentityBackup,
	onProgress?: SyncProgressCallback
): Promise<SyncResult> {
	try {
		validateIdentityBackup(backup);
		if (isSeedIdentityBackup(backup)) {
			return await syncSeedInventory(backup, onProgress);
		}
		// Type 42 backups
		if (isType42Backup(backup)) {
			logger.info("Type 42 backup detected");

			// NEW Type 42 wallet (no ids yet) — create first identity
			if (!backup.ids || backup.ids.trim() === "") {
				logger.warn(
					"NEW Type 42 wallet with no identities - creating first identity"
				);
				return await syncNewType42Wallet(
					backup as BapMasterBackup & { rootPk: string },
					onProgress
				);
			}

			// Existing Type 42 wallet with identities
			return await syncExistingType42Wallet(
				backup as BapMasterBackup & { rootPk: string; ids: string },
				onProgress
			);
		}

		// Legacy xprv backup
		if (!isLegacyBackup(backup)) {
			throw new Error("A master backup is required");
		}
		return await syncLegacyBackupIds(backup, onProgress);
	} catch {
		logger.error("Failed to sync Bitcoin identities from backup");
		const errorMsg = "Failed to sync identities from backup";
		onProgress?.("Failed to sync identities", "error", errorMsg);
		return {
			success: false,
			count: 0,
			error: errorMsg,
		};
	}
}

/**
 * Attempt to derive missing identities from the BAP master key,
 * verifying each matches a known DB record.
 */
function attemptIdentityRecovery(
	bap: InstanceType<typeof BAP>,
	missingFromBackup: Array<{
		identity_key: string;
		name: string | null;
		counter: number | null;
	}>,
	dbIdKeys: Set<string>
): number {
	let recovered = 0;

	for (const missing of missingFromBackup) {
		try {
			const newId = bap.newId();
			const derivedKey = newId.bapId;

			if (derivedKey === missing.identity_key) {
				recovered++;
				logger.info("Recovered an identity");
			} else if (dbIdKeys.has(derivedKey)) {
				recovered++;
				logger.info("Recovered an identity in a different order");
			} else {
				logger.warn("Identity derivation mismatch; stopping recovery");
				bap.removeId(derivedKey);
				break;
			}
		} catch {
			logger.warn("Failed to derive identity for recovery");
			break;
		}
	}

	return recovered;
}

/**
 * Recover identities that are registered in the database but missing from the
 * current backup. This handles stale backups (PRF biometric unlock, old backup
 * file, cloud restore) where the user created identities after the backup was
 * last saved.
 *
 * For master backups, identities are derived deterministically from the master
 * key. We derive sequential identities and verify they match the database
 * records, then add them to the backup.
 *
 * Uses the BAP instance from sessionStorage (already populated during sign-in).
 *
 * @param onProgress - Optional progress callback
 * @returns Number of recovered identities
 */
export async function recoverMissingIdentitiesFromDB(
	onProgress?: SyncProgressCallback
): Promise<number> {
	try {
		// Fetch all registered identities from the database
		const response = await fetch("/api/user/bap-ids", {
			credentials: "include",
		});

		if (!response.ok) {
			logger.warn("Failed to fetch registered BAP IDs for recovery check");
			return 0;
		}

		const { bapIds } = (await response.json()) as {
			bapIds: Array<{
				identity_key: string;
				name: string | null;
				counter: number | null;
			}>;
		};

		if (!bapIds || bapIds.length === 0) {
			return 0;
		}

		// Get current BAP instance (reflects what's in sessionStorage backup)
		const bap = getBAPInstance();
		if (!bap) {
			return 0;
		}

		const backupIdKeys = new Set(bap.listIds());
		const dbIdKeys = new Set(bapIds.map((b) => b.identity_key));

		// Find identities in DB that are NOT in the backup
		const missingFromBackup = bapIds
			.filter((dbId) => !backupIdKeys.has(dbId.identity_key))
			.sort((a, b) => (a.counter ?? 0) - (b.counter ?? 0));

		if (missingFromBackup.length === 0) {
			return 0;
		}

		logger.info(
			`Found ${missingFromBackup.length} identities in DB missing from backup - attempting recovery`
		);
		onProgress?.(
			`Recovering ${missingFromBackup.length} missing identities`,
			"in_progress"
		);

		const recovered = attemptIdentityRecovery(bap, missingFromBackup, dbIdKeys);

		if (recovered > 0) {
			// Update backup with recovered identities
			const updatedIds = bap.exportIds();
			await updateBapIdsInBackup(updatedIds);

			logger.info(`Successfully recovered ${recovered} identities into backup`);
			onProgress?.(`Recovered ${recovered} missing identities`, "completed");

			// Dispatch event so identity context reloads
			if (typeof window !== "undefined") {
				window.dispatchEvent(new Event("bap-backup-updated"));
			}
		} else {
			onProgress?.("No identities could be recovered", "completed");
		}

		return recovered;
	} catch {
		logger.warn("Identity recovery from the server failed (non-fatal)");
		return 0;
	}
}

async function recoverLegacyOAuthClientOwnership(
	backup: BapMasterBackup
): Promise<number> {
	const offer = readReissuedBackupOffer();
	const mappings = offer?.mappings?.filter(
		(mapping) => mapping.oldBapId !== mapping.newBapId
	);
	if (!mappings?.length) {
		return 0;
	}

	let migrated = 0;
	for (const mapping of mappings) {
		try {
			const body = serializeLegacyOAuthOwnerRecoveryBody({
				oldBapId: mapping.oldBapId,
				newBapId: mapping.newBapId,
			});
			const authToken = createAuthTokenFromBackup(
				{
					wif: extractWifFromBackup(backup, mapping.newBapId),
					id: mapping.newBapId,
				},
				LEGACY_OAUTH_OWNER_RECOVERY_PATH,
				body
			);
			const response = await fetch(LEGACY_OAUTH_OWNER_RECOVERY_PATH, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bitcoin-Auth ${authToken}`,
				},
				body,
			});

			if (!response.ok) {
				logger.warn("Legacy OAuth ownership recovery failed");
				continue;
			}

			const result =
				(await response.json()) as LegacyOAuthOwnerRecoveryResponse;
			migrated += result.migrated;
		} catch {
			logger.warn("Legacy OAuth ownership recovery skipped");
		}
	}

	if (migrated > 0) {
		logger.info(`Recovered ${migrated} legacy OAuth client ownership row(s)`);
	}

	return migrated;
}

/**
 * Activate an account for same-tab signing.
 *
 * This must not start a WebAuthn/PRF ceremony. OAuth sign-in and identity
 * selection both call this helper; prompting here stacks hidden passkey prompts
 * on top of the user's explicit unlock. Cross-origin signer iframes can still
 * decrypt the PRF-encrypted backup if they later need wallet access.
 */
export function activateAccount(bapId: string): { wif: string; id: string } {
	const backupStr = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
	if (!backupStr) {
		throw new Error("No decrypted backup in session");
	}
	const backup = validateIdentityBackup(JSON.parse(backupStr));
	const account = { wif: extractWifFromBackup(backup, bapId), id: bapId };

	// Fast path: same-tab sessionStorage
	sessionStorage.setItem(STORAGE_KEYS.ACTIVE_ACCOUNT, JSON.stringify(account));

	return account;
}

export interface SignInResult {
	ok?: boolean;
	error?: string;
	errorCode?: string;
	/** Server-directed continuation; callers may opt to perform the navigation. */
	redirectURI?: string;
	data?: {
		user: {
			id: string;
			email?: string | null;
			name: string;
			pubkey?: string | null;
			emailVerified?: boolean;
			image?: string | null;
			createdAt: Date;
			updatedAt: Date;
		};
		session: {
			id: string;
			userId: string;
			expiresAt: Date;
			token: string;
			ipAddress?: string | null;
			userAgent?: string | null;
		};
		code?: string; // OAuth authorization code
	};
	syncedIdentities?: Array<{ bapId: string; counter: number; name?: string }>;
	requestId?: string;
	retryable?: boolean;
}

/**
 * Perform post-sign-in identity sync for master backups.
 * Syncs BAP IDs from backup, recovers missing identities from DB,
 * triggers background blockchain scan, and creates member backups.
 */
async function performPostSignInSync(
	backup: BapMasterBackup,
	onSyncProgress?: SyncProgressCallback
): Promise<Array<{ bapId: string; counter: number; name?: string }>> {
	// 1. Sync BAP IDs from backup file (Critical Path)
	const firstSyncResult = await syncBapIdsFromBackup(backup, onSyncProgress);

	// 2. Recover OAuth clients pinned to pre-0.3 BAP IDs after reissue.
	await recoverLegacyOAuthClientOwnership(backup);

	// 3. Recover any identities registered in DB but missing from backup
	let syncedIdentities = firstSyncResult.identities || [];
	try {
		const recoveredCount = await recoverMissingIdentitiesFromDB(onSyncProgress);
		if (recoveredCount > 0) {
			const updatedBackupStr = sessionStorage.getItem(
				STORAGE_KEYS.DECRYPTED_BACKUP
			);
			if (updatedBackupStr) {
				const updatedBackup = JSON.parse(updatedBackupStr);
				const reSync = await syncBapIdsFromBackup(updatedBackup);
				if (reSync.identities) {
					syncedIdentities = reSync.identities;
				}
			}
		}
	} catch {
		logger.warn("Identity recovery from the server failed (non-fatal)");
	}

	// 4. Scan blockchain for published identities (Background Task)
	syncIdentitiesFromChain()
		.then(async (newCount) => {
			if (newCount > 0) {
				logger.info(
					`Discovered ${newCount} new on-chain identities (background)`
				);
				const updatedBackupStr = sessionStorage.getItem(
					STORAGE_KEYS.DECRYPTED_BACKUP
				);
				if (updatedBackupStr) {
					const updatedBackup = JSON.parse(updatedBackupStr);
					await syncBapIdsFromBackup(updatedBackup);
				}
			}
		})
		.catch(() => {
			logger.warn("Background blockchain sync failed");
		});

	return syncedIdentities;
}

export interface BackupIdentityReconciliationResult {
	synced: number;
	recoveredFromServer: number;
	discoveredOnChain: number;
}

/**
 * Reconcile an already-unlocked master backup with every source of identity
 * truth we can access from the browser session:
 *
 * 1. Register identities present in the backup with this Sigma server.
 * 2. Recover identities this server knows about but the backup is missing.
 * 3. Scan deterministic BAP identities published on-chain but unknown here.
 *
 * This is safe to run outside the sign-in path. It updates the active
 * decrypted backup immediately and persists an encrypted copy only when a
 * password is already available to the caller.
 */
export async function reconcileUnlockedBackupIdentities(
	onSyncProgress?: SyncProgressCallback
): Promise<BackupIdentityReconciliationResult> {
	const backupStr = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
	if (!backupStr) {
		return { synced: 0, recoveredFromServer: 0, discoveredOnChain: 0 };
	}

	let backup: IdentityBackup;
	try {
		backup = validateIdentityBackup(JSON.parse(backupStr));
	} catch {
		return { synced: 0, recoveredFromServer: 0, discoveredOnChain: 0 };
	}

	if (isSeedIdentityBackup(backup)) {
		// Restored seed inventories are read-only here. Unknown server profiles
		// must never be enrolled by an automatic reconciliation effect.
		const registered = await fetchServerBapIds().catch(() => []);
		const keys = new Set(
			registered.flatMap((entry) =>
				entry &&
				typeof entry === "object" &&
				"identity_key" in entry &&
				typeof entry.identity_key === "string"
					? [entry.identity_key]
					: []
			)
		);
		return {
			synced: backup.profiles.filter((profile) => keys.has(profile.bapId))
				.length,
			recoveredFromServer: 0,
			discoveredOnChain: 0,
		};
	}
	if (!(isLegacyBackup(backup) || isType42Backup(backup))) {
		return { synced: 0, recoveredFromServer: 0, discoveredOnChain: 0 };
	}

	// The identity context schedules this the moment a decrypted backup
	// appears, which during /signup is before the sign-up request has
	// returned. Without a server session every registration below is a 401
	// and the failed draft save used to consume the typed display name
	// (OPL-4238). Sign-in runs its own sync once the session exists.
	try {
		await fetchServerBapIds();
	} catch {
		logger.info("No server session yet; skipping identity reconciliation");
		return { synced: 0, recoveredFromServer: 0, discoveredOnChain: 0 };
	}
	const syncResult = await syncBapIdsFromBackup(backup, onSyncProgress);
	let synced = syncResult.count ?? 0;

	const recoveredFromServer =
		await recoverMissingIdentitiesFromDB(onSyncProgress);
	if (recoveredFromServer > 0) {
		const updatedBackupStr = sessionStorage.getItem(
			STORAGE_KEYS.DECRYPTED_BACKUP
		);
		if (updatedBackupStr) {
			const updatedBackup = JSON.parse(updatedBackupStr) as BapMasterBackup;
			const reSync = await syncBapIdsFromBackup(updatedBackup);
			synced = reSync.count ?? synced;
		}
	}

	const discoveredOnChain = await syncIdentitiesFromChain(onSyncProgress);
	if (discoveredOnChain > 0) {
		const updatedBackupStr = sessionStorage.getItem(
			STORAGE_KEYS.DECRYPTED_BACKUP
		);
		if (updatedBackupStr) {
			const updatedBackup = JSON.parse(updatedBackupStr) as BapMasterBackup;
			const reSync = await syncBapIdsFromBackup(updatedBackup);
			synced = reSync.count ?? synced;
		}
	}

	return { synced, recoveredFromServer, discoveredOnChain };
}

/**
 * Ensure master backups have a real identity before local sigma sign-in.
 *
 * Freshly generated Type42/legacy backups can have no IDs yet. If we authenticate
 * with the root key in that state, later sign-ins use the member key and Better
 * Auth treats them as a different user. Create the first identity up front so the
 * auth token is always signed by the stable member key.
 */
async function prepareBackupForSignIn(
	backup: IdentityBackup,
	requestedBapId?: string,
	allowIdentityCreation = true
): Promise<{
	backup: IdentityBackup;
	bapId?: string;
}> {
	validateIdentityBackup(backup);
	if (isSeedIdentityBackup(backup)) {
		const bapId = requestedBapId ?? backup.profiles[0].bapId;
		if (!new SeedProfileAdapter(backup).getId(bapId)) {
			throw new Error("Requested seed profile is not in this backup");
		}
		return { backup, bapId };
	}
	if (!(isLegacyBackup(backup) || isType42Backup(backup))) {
		return { backup, bapId: requestedBapId };
	}

	if (requestedBapId) {
		return { backup, bapId: requestedBapId };
	}

	const bap = initializeBAPFromBackup(backup);
	if (!bap) {
		return { backup };
	}

	const existingIds = bap.listIds();
	if (existingIds.length > 0) {
		return { backup, bapId: existingIds[0] };
	}
	if (!allowIdentityCreation) {
		throw new Error("The backup does not contain an existing identity");
	}

	const firstIdentity = bap.newId();
	const updatedBackup: BapMasterBackup = {
		...backup,
		ids: bap.exportIds(),
	};

	const password =
		sessionStorage.getItem(STORAGE_KEYS.TEMP_PASSWORD) || undefined;
	// No session exists yet at this point — flushing the cloud copy here can
	// only 401. The post-sign-in path picks up the stale mark instead.
	await syncBackupToStorage(updatedBackup, password, { deferCloudFlush: true });

	return {
		backup: updatedBackup,
		bapId: firstIdentity.bapId,
	};
}

/**
 * Map a Better Auth error result to a SignInResult.
 */
function mapSignInError(error: {
	code?: string;
	message?: string;
	request_id?: string;
	retryable?: boolean;
	status?: number;
}): SignInResult {
	if (error.status === 429) {
		return {
			ok: false,
			error: "Too many sign-in attempts. Wait a minute and try again.",
			errorCode: "RATE_LIMITED",
			requestId: error.request_id,
			retryable: true,
		};
	}
	return {
		ok: false,
		error: "Sign in failed. Please try again.",
		// Keep the server code so the login page can offer enrollment for an
		// unregistered key instead of a dead end; the message stays generic.
		errorCode: error.code,
		requestId: "request_id" in error ? error.request_id : undefined,
		retryable: "retryable" in error ? error.retryable : false,
	};
}

function mapRestoreSignInError(error: {
	code?: string;
	message?: string;
	request_id?: string;
	retryable?: boolean;
}): SignInResult {
	return {
		ok: false,
		error: error.message || "This backup could not be restored.",
		errorCode: error.code,
		requestId: error.request_id,
		retryable: error.retryable === true,
	};
}

function mapSigmaSignInError(
	error: NonNullable<SignInSigmaResponse["error"]>,
	restoreExistingProfile: boolean
): SignInResult {
	return restoreExistingProfile
		? mapRestoreSignInError(error)
		: mapSignInError(error);
}

interface SignInSigmaResponse {
	code?: string;
	token?: string;
	user?: {
		id: string;
		pubkey?: string;
		name?: string;
	};
	redirectURI?: string;
	message?: string;
	request_id?: string;
	retryable?: boolean;
	error?: {
		code?: string;
		message?: string;
		request_id?: string;
		retryable?: boolean;
		/** HTTP status of a non-2xx response without a structured error body. */
		status?: number;
	};
}

function handleSigmaRedirect(
	redirectURI: string | undefined,
	navigate: boolean
): SignInResult | null {
	if (typeof redirectURI !== "string") {
		return null;
	}
	if (navigate) {
		logger.info("OAuth redirect from Better Auth, navigating to callback");
		window.location.href = redirectURI;
	}
	return { ok: true, redirectURI };
}

async function postSigmaSignIn(
	authToken: string,
	body: { bapId: string; intent?: "sign-up" },
	restoreExistingProfile: boolean,
	enrollmentProof?: string
): Promise<SignInSigmaResponse> {
	let requestPath = SIGMA_SIGNIN_REQUEST_PATH;
	if (restoreExistingProfile) {
		requestPath = "/api/auth/restore-sigma";
	} else if (body.intent === "sign-up") {
		requestPath = SIGMA_SIGNUP_REQUEST_PATH;
	}
	const response = await fetch(requestPath, {
		method: "POST",
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
			"X-Auth-Token": authToken,
			...(enrollmentProof
				? { "X-Sigma-Enrollment-Proof": enrollmentProof }
				: {}),
		},
		body: restoreExistingProfile
			? JSON.stringify({ bapId: body.bapId })
			: serializeSigmaSigninBody(body),
	});
	// A rate-limited or proxy response may not be JSON at all.
	const parsed = (await response
		.json()
		.catch(() => ({}))) as SignInSigmaResponse;
	if (!(response.ok || parsed.error)) {
		parsed.error = {
			code: parsed.code,
			message: parsed.message || "Sign in failed",
			request_id: parsed.request_id,
			retryable: parsed.retryable,
			status: response.status,
		};
	} else if (parsed.error && !response.ok) {
		parsed.error.status = response.status;
	}
	return parsed;
}

function createSigmaSigninRequest(
	signingBackup: { wif: string; id: string },
	bapId: string,
	restoreExistingProfile: boolean,
	enrollNewProfile: boolean
): {
	authToken: string;
	body: { bapId: string; intent?: "sign-up" };
	enrollmentProof?: string;
} {
	const body = {
		bapId,
		...(enrollNewProfile ? { intent: "sign-up" as const } : {}),
	};
	if (restoreExistingProfile) {
		return {
			authToken: createAuthTokenFromBackup(
				signingBackup,
				"/api/auth/restore-sigma"
			),
			body,
		};
	}

	const signedBody = serializeSigmaSigninBody(body);
	if (enrollNewProfile) {
		return {
			authToken: createAuthTokenFromBackup(
				signingBackup,
				SIGMA_SIGNIN_REQUEST_PATH
			),
			body,
			enrollmentProof: createAuthTokenFromBackup(
				signingBackup,
				SIGMA_SIGNUP_REQUEST_PATH,
				signedBody
			),
		};
	}

	return {
		authToken: createAuthTokenFromBackup(
			signingBackup,
			SIGMA_SIGNIN_REQUEST_PATH,
			signedBody
		),
		body,
	};
}

async function syncIdentitiesIfApplicable(
	signInBackup: IdentityBackup,
	onSyncProgress?: SyncProgressCallback
): Promise<Array<{ bapId: string; counter: number; name?: string }>> {
	if (isSeedIdentityBackup(signInBackup)) {
		return (
			(await syncSeedInventory(signInBackup, onSyncProgress)).identities ?? []
		);
	}
	if (!(isLegacyBackup(signInBackup) || isType42Backup(signInBackup))) {
		return [];
	}
	try {
		return await performPostSignInSync(signInBackup, onSyncProgress);
	} catch {
		logger.warn("Failed to sync Bitcoin identities (non-fatal)");
		return [];
	}
}

const IDENTITY_MAPPING_RETRYABLE_ERROR =
	"Sign in could not register your identity. Please retry sign-in.";

interface ServerBapIdsResponse {
	bapIds?: unknown[];
}

type IdentityMappingCheckResult =
	| { ok: true }
	| { ok: false; retryable: true; error: string };

export interface EnsureIdentityMappingAfterSignInDeps {
	fetchBapIds: () => Promise<unknown[]>;
	register: () => Promise<unknown>;
	signOut: () => Promise<unknown>;
	removeDecryptedBackup?: () => void;
}

async function fetchServerBapIds(): Promise<unknown[]> {
	const response = await fetch("/api/user/bap-ids", {
		credentials: "include",
	});
	if (!response.ok) {
		throw new Error("Failed to verify identity mappings");
	}
	const parsed = (await response.json()) as ServerBapIdsResponse;
	return Array.isArray(parsed.bapIds) ? parsed.bapIds : [];
}

async function signOutSigmaSession(): Promise<void> {
	await signOutBetterAuthSession();
}

function removeDecryptedBackupFromSession(): void {
	clearSensitiveSessionStorage();
}

async function hasServerIdentityMapping(
	fetchBapIds: () => Promise<unknown[]>
): Promise<boolean> {
	try {
		const bapIds = await fetchBapIds();
		return bapIds.length > 0;
	} catch {
		logger.warn("Identity mapping verification failed");
		return false;
	}
}

export async function ensureIdentityMappingAfterSignIn(
	deps: EnsureIdentityMappingAfterSignInDeps
): Promise<IdentityMappingCheckResult> {
	if (await hasServerIdentityMapping(deps.fetchBapIds)) {
		return { ok: true };
	}

	try {
		await deps.register();
	} catch {
		logger.warn("Identity mapping retry registration failed");
	}

	if (await hasServerIdentityMapping(deps.fetchBapIds)) {
		return { ok: true };
	}

	await deps.signOut();
	(deps.removeDecryptedBackup ?? removeDecryptedBackupFromSession)();

	return {
		ok: false,
		retryable: true,
		error: IDENTITY_MAPPING_RETRYABLE_ERROR,
	};
}

export async function registerBackupIdentitiesForCurrentSession(
	backup: IdentityBackup,
	selectedBapId?: string,
	onSyncProgress?: SyncProgressCallback
): Promise<Array<{ bapId: string; counter: number; name?: string }>> {
	validateIdentityBackup(backup);
	if (isSeedIdentityBackup(backup)) {
		return (await syncSeedInventory(backup, onSyncProgress)).identities ?? [];
	}
	if (isLegacyBackup(backup) || isType42Backup(backup)) {
		return await performPostSignInSync(backup, onSyncProgress);
	}

	const identity = extractIdentityFromBackup(backup, selectedBapId);
	// Use the display name the signup form stored (INITIAL_PROFILE_DATA) so a
	// new identity is not registered as a generic "Identity 1" / raw address
	// (OPL-4238), and persist it as the draft profile like the legacy paths do.
	const identityName = getInitialIdentityName();
	await registerBapIdentity({
		bapId: identity.id,
		name: identityName,
		counter: 0,
		isPrimary: true,
		accountPubkey: identity.pubkey,
	});
	await saveInitialProfileData(identity.id);

	return [{ bapId: identity.id, counter: 0, name: identityName }];
}

function restoreSessionStorageValue(key: string, previousValue: string | null) {
	if (previousValue === null) {
		sessionStorage.removeItem(key);
		return;
	}
	sessionStorage.setItem(key, previousValue);
}

function rollbackSignInCandidate(
	restoreExistingProfile: boolean,
	previousDecryptedBackup: string | null,
	previousActiveAccount: string | null
) {
	if (!restoreExistingProfile) {
		// Preserve ordinary sign-in behavior: a failed candidate is cleared.
		sessionStorage.removeItem(STORAGE_KEYS.DECRYPTED_BACKUP);
		return;
	}
	restoreSessionStorageValue(
		STORAGE_KEYS.DECRYPTED_BACKUP,
		previousDecryptedBackup
	);
	restoreSessionStorageValue(
		STORAGE_KEYS.ACTIVE_ACCOUNT,
		previousActiveAccount
	);
}

function markEnrollmentCloudBackupPending(
	enrollNewProfile: boolean,
	bapId: string
): void {
	if (enrollNewProfile) {
		markNewWalletCloudBackupPending(bapId);
	}
}

function signInCandidateState(
	backup: IdentityBackup,
	restoreExistingProfile: boolean,
	persistLocalState: boolean
) {
	if (!persistLocalState) {
		return {
			write: validateIdentityBackup,
			rollback() {
				/* Proof-only mode has no candidate storage to restore. */
			},
		};
	}
	const preserve = restoreExistingProfile || isSeedIdentityBackup(backup);
	const previousBackup = preserve
		? sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP)
		: null;
	const previousAccount = preserve
		? sessionStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT)
		: null;
	let written = false;
	return {
		write(value: IdentityBackup) {
			validateIdentityBackup(value);
			written = true;
			sessionStorage.setItem(
				STORAGE_KEYS.DECRYPTED_BACKUP,
				JSON.stringify(value)
			);
		},
		rollback() {
			if (written) {
				rollbackSignInCandidate(preserve, previousBackup, previousAccount);
			}
		},
	};
}

function assertSignInMode(
	restore: boolean,
	enroll: boolean,
	persist: boolean,
	backup: IdentityBackup
): void {
	if (!persist && enroll && isSeedIdentityBackup(backup)) {
		throw new Error("Imported seed profiles cannot enroll through login");
	}
	if (restore && enroll) {
		throw new Error("Restore cannot enroll a new profile");
	}
	if (!(persist || restore || enroll)) {
		throw new Error("In-memory proof requires existing-profile restore");
	}
}

function signingAccountForCandidate(
	identity: ReturnType<typeof extractIdentityFromBackup>,
	persist: boolean
): { wif: string; id: string } {
	return persist
		? activateAccount(identity.bapId)
		: { wif: identity.privateKey, id: identity.bapId };
}

/** Name and message of a thrown value, for structured logs (never keys). */
function describeError(error: unknown): {
	errorName: string;
	errorMessage: string;
} {
	if (error instanceof Error) {
		return { errorName: error.name, errorMessage: error.message };
	}
	return { errorName: "UnknownError", errorMessage: String(error) };
}

/**
 * Sign in with backup using the custom sigma endpoint
 * Accepts master backups or member backups
 * Master backups are automatically converted to member backups for signing
 *
 * IMPORTANT: This function just creates a session. If called during an OAuth flow,
 * Better Auth's hooks.after will automatically detect the oidc_login_prompt cookie
 * and continue the OAuth flow. DO NOT manually handle OAuth parameters here.
 */
export async function signInWithBackup(
	backup: IdentityBackup,
	options?: {
		bapId?: string; // Which BAP identity to use (for multi-identity wallets)
		onSyncProgress?: SyncProgressCallback;
		/** Require an exact, already-registered profile authority match. */
		restoreExistingProfile?: boolean;
		/** Restore-only proof mode: caller commits the authenticated candidate afterward. */
		persistLocalState?: boolean;
		/** Return a server continuation instead of navigating inside this helper. */
		navigateOnRedirect?: boolean;
		/** Explicitly authorize creation/enrollment from the /signup flow. */
		enrollNewProfile?: boolean;
	}
): Promise<SignInResult> {
	const restoreExistingProfile = options?.restoreExistingProfile === true;
	const enrollNewProfile = options?.enrollNewProfile === true;
	const persistLocalState = options?.persistLocalState !== false;
	const candidate = signInCandidateState(
		backup,
		restoreExistingProfile,
		persistLocalState
	);

	try {
		assertSignInMode(
			restoreExistingProfile,
			enrollNewProfile,
			persistLocalState,
			backup
		);
		validateIdentityBackup(backup);
		const prepared = await prepareBackupForSignIn(
			backup,
			options?.bapId,
			enrollNewProfile && persistLocalState
		);
		const signInBackup = prepared.backup;

		candidate.write(signInBackup);

		const identity = extractIdentityFromBackup(
			signInBackup,
			prepared.bapId ?? options?.bapId
		);
		const selectedBapId = prepared.bapId ?? options?.bapId ?? identity.id;
		if (persistLocalState) {
			markEnrollmentCloudBackupPending(enrollNewProfile, selectedBapId);
		}
		logger.info("Signing in with Sigma identity");

		const signingBackup = signingAccountForCandidate(
			identity,
			persistLocalState
		);
		const request = createSigmaSigninRequest(
			signingBackup,
			selectedBapId,
			restoreExistingProfile,
			enrollNewProfile
		);

		const result = await postSigmaSignIn(
			request.authToken,
			request.body,
			restoreExistingProfile,
			request.enrollmentProof
		);

		if (result.error) {
			candidate.rollback();
			return mapSigmaSignInError(result.error, restoreExistingProfile);
		}

		// Better Auth returns { redirectURI } for OAuth flows — navigate to it.
		const redirectResult = handleSigmaRedirect(
			result.redirectURI,
			options?.navigateOnRedirect !== false
		);
		if (redirectResult) {
			return redirectResult;
		}
		if (!persistLocalState || restoreExistingProfile || !enrollNewProfile) {
			// Existing-profile sign-in and restore are session-only operations. The
			// server has already selected the exact stored owner and created the
			// session, so never follow them with registration, migration, recovery,
			// backup rewriting, organization, or cloud-refresh mutation paths.
			return { ok: true };
		}

		// Discover and register all BAP IDs from the backup. Only legacy/Type42
		// backups need this — modern member backups are single-identity.
		const syncedIdentities = await syncIdentitiesIfApplicable(
			signInBackup,
			options?.onSyncProgress
		);

		const mappingResult = await ensureIdentityMappingAfterSignIn({
			fetchBapIds: fetchServerBapIds,
			register: () =>
				registerBackupIdentitiesForCurrentSession(
					signInBackup,
					selectedBapId,
					options?.onSyncProgress
				),
			signOut: signOutSigmaSession,
			removeDecryptedBackup: removeDecryptedBackupFromSession,
		});
		if (!mappingResult.ok) {
			return mappingResult;
		}

		// This branch only runs for explicit new-profile enrollment (signup), so
		// cloud backup is enabled by default here (OPL-4240): the encrypted blob
		// is uploaded even when no cloud row exists yet. Imported/restored
		// wallets keep the opt-in flush semantics.
		const cloudRefreshResult = await enableNewWalletCloudBackup(selectedBapId);
		if (cloudRefreshResult.status !== "flushed") {
			logger.warn(
				`Post-sign-up cloud backup enable did not flush (${cloudRefreshResult.status})`
			);
			return {
				ok: false,
				error:
					"Your wallet was created, but cloud backup could not be enabled. Retry to finish safely.",
				errorCode: "CLOUD_BACKUP_ENABLE_FAILED",
				retryable: true,
			};
		}

		return { ok: true, syncedIdentities };
	} catch (error) {
		logger.error(
			{ operation: "sign_in_with_backup", ...describeError(error) },
			"Sign-in failed"
		);
		candidate.rollback();
		return {
			ok: false,
			error:
				restoreExistingProfile && error instanceof Error
					? error.message
					: "Sign in failed. Please try again.",
		};
	}
}

/**
 * Handle failed login by creating user record
 */
export function handleFailedLogin(
	backup: BapMasterBackup,
	encryptedBackup: string
) {
	// This would typically create a user record in your system
	// For now, we'll just log the attempt
	logger.info("Creating user record for Sigma identity");

	// Store the encrypted backup for future use
	localStorage.setItem(STORAGE_KEYS.ENCRYPTED_BACKUP, encryptedBackup);
	markCloudBackupStale();

	// Try to sign in again after user creation
	return signInWithBackup(backup);
}
